<script setup>
import { ref, computed } from 'vue'
import { useForm } from '@inertiajs/vue3'

const props = defineProps({
  client_nom:         { type: String, default: 'auRapport' },
  client_logo:        { type: String, default: null },
  client_logo_is_url: { type: Boolean, default: false },
  alert:              { type: String, default: null },
  login_url:          { type: String, default: '/utilisateurs/sign_in' },
  param_key:          { type: String, default: 'utilisateur' },
  password_reset_url: { type: String, default: '/utilisateurs/password/new' },
})

const logoSrc = computed(() => {
  if (!props.client_logo) return null
  return props.client_logo_is_url ? props.client_logo : `/images/client/${props.client_logo}`
})

const showPassword = ref(false)

const form = useForm({
  email:       '',
  password:    '',
  remember_me: false,
})

function submit() {
  form.transform(data => ({ [props.param_key]: data })).post(props.login_url)
}

const currentYear = new Date().getFullYear()
</script>

<template>
  <div class="min-h-screen bg-surface text-on-surface font-body flex flex-col">

    <!-- Split layout -->
    <div class="flex flex-1">

      <!-- Panneau gauche — branding -->
      <div class="hidden lg:flex lg:w-[55%] relative flex-col items-center justify-center px-16 bg-surface overflow-hidden">

        <!-- Blurs ambiants -->
        <div class="absolute top-0 right-0 w-[500px] h-[500px] bg-primary-container rounded-full blur-[140px] opacity-10 -translate-y-1/3 translate-x-1/3 pointer-events-none"></div>
        <div class="absolute bottom-0 left-0 w-[350px] h-[350px] bg-tertiary-container rounded-full blur-[120px] opacity-10 translate-y-1/3 -translate-x-1/3 pointer-events-none"></div>

        <!-- Contenu branding -->
        <div class="relative z-10 flex flex-col items-start gap-8 max-w-md w-full">

          <!-- Logo ou icône shield -->
          <div class="flex items-center gap-4">
            <img
              v-if="client_logo"
              :src="logoSrc"
              alt="Logo"
              class="h-12 w-auto object-contain"
            />
            <span v-else class="material-symbols-outlined text-primary text-5xl">shield</span>
          </div>

          <!-- Nom du client -->
          <h1 class="text-5xl font-black tracking-widest uppercase text-primary leading-tight">
            {{ client_nom }}
          </h1>

          <!-- Tagline -->
          <p class="text-on-surface-variant text-lg leading-relaxed max-w-sm">
            Gestion de projet et surveillance de site en temps réel.
          </p>

          <!-- Ligne déco -->
          <div class="w-16 h-1 rounded-full machined-sheen"></div>
        </div>
      </div>

      <!-- Panneau droit — formulaire -->
      <div class="w-full lg:w-[45%] flex flex-col items-center justify-center px-8 py-12 bg-surface-container-low relative">

        <!-- Header mobile uniquement -->
        <div class="lg:hidden flex items-center gap-3 mb-10 self-start">
          <span class="material-symbols-outlined text-primary text-3xl">shield</span>
          <h1 class="text-primary font-black tracking-widest uppercase text-lg">{{ client_nom }}</h1>
        </div>

        <!-- Card formulaire -->
        <div class="w-full max-w-md bg-surface-container rounded-xl shadow-2xl overflow-hidden">

          <!-- En-tête card -->
          <div class="px-8 pt-8 pb-4 text-center">
            <h2 class="text-2xl font-bold tracking-tight text-on-surface mb-2">
              Accès sécurisé
            </h2>
            <p class="text-on-surface-variant text-sm">
              Connectez-vous pour accéder à vos projets.
            </p>
          </div>

          <!-- Alerte erreur -->
          <div v-if="alert || form.errors?.base" class="mx-8 mt-2 px-4 py-3 rounded-lg bg-error-container text-on-error-container text-sm flex items-center gap-2">
            <span class="material-symbols-outlined text-base">error</span>
            <span>{{ alert || form.errors?.base }}</span>
          </div>

          <!-- Formulaire -->
          <form class="px-8 pt-6 pb-8 space-y-6" @submit.prevent="submit">

            <!-- Email -->
            <div class="space-y-2">
              <label class="block text-xs font-bold uppercase tracking-wider text-outline" for="email">
                Identifiant ou Email
              </label>
              <div class="relative group">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span class="material-symbols-outlined text-outline group-focus-within:text-primary transition-colors text-xl">person</span>
                </div>
                <input
                  id="email"
                  v-model="form.email"
                  type="text"
                  name="email"
                  placeholder="nom@entreprise.com"
                  autocomplete="email"
                  class="block w-full pl-10 pr-3 py-3 bg-surface-container-highest border-none rounded-lg text-on-surface placeholder-outline focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                />
              </div>
            </div>

            <!-- Mot de passe -->
            <div class="space-y-2">
              <div class="flex justify-between items-center">
                <label class="block text-xs font-bold uppercase tracking-wider text-outline" for="password">
                  Mot de passe
                </label>
                <a :href="password_reset_url" class="text-xs text-primary font-bold hover:brightness-125 transition-all">
                  Mot de passe oublié ?
                </a>
              </div>
              <div class="relative group">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span class="material-symbols-outlined text-outline group-focus-within:text-primary transition-colors text-xl">lock</span>
                </div>
                <input
                  id="password"
                  v-model="form.password"
                  :type="showPassword ? 'text' : 'password'"
                  name="password"
                  placeholder="••••••••"
                  autocomplete="current-password"
                  class="block w-full pl-10 pr-10 py-3 bg-surface-container-highest border-none rounded-lg text-on-surface placeholder-outline focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                />
                <button
                  type="button"
                  class="absolute inset-y-0 right-0 pr-3 flex items-center text-outline hover:text-on-surface transition-colors"
                  @click="showPassword = !showPassword"
                >
                  <span class="material-symbols-outlined text-xl">
                    {{ showPassword ? 'visibility_off' : 'visibility' }}
                  </span>
                </button>
              </div>
            </div>

            <!-- Bouton submit -->
            <div class="pt-2">
              <button
                type="submit"
                :disabled="form.processing"
                class="machined-sheen w-full py-4 px-6 rounded-lg text-on-primary font-black tracking-widest uppercase flex items-center justify-center gap-2 hover:brightness-110 active:scale-95 transition-all shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span v-if="form.processing" class="material-symbols-outlined text-xl animate-spin">progress_activity</span>
                <span v-else>Se connecter</span>
                <span v-if="!form.processing" class="material-symbols-outlined text-xl">arrow_forward</span>
              </button>
            </div>
          </form>

          <!-- Pied de card -->
          <div class="bg-surface-container-low px-8 py-5 text-center">
            <p class="text-on-surface-variant text-xs">
              Problèmes de connexion ? Contactez le support technique.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <footer class="py-6 text-center text-outline text-[11px] tracking-widest uppercase bg-surface">
      Copyright &copy; 2016-{{ currentYear }} aurapport.com inc.
    </footer>
  </div>
</template>
